import React, { useEffect, useState } from "react"
import { Button, Card, CardBody, CardTitle, Col, Label, Row } from "reactstrap"
import { Formik, Form, Field, ErrorMessage } from "formik"
import { useNavigate } from "react-router-dom"
import {
  addEmailsSchema,
  loyaltyPointInfoSchema,
} from "components/Common/validation"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"

import Spinners from "components/Shared/Spinners"

import { showAlert } from "store/reducers/alertReducer"
import {
  clearMenuCache,
  getForceUpdateData,
  getGlobalSettingByType,
  updateGlobalSettingByType,
} from "store/actions/globalSettingAction"
import isPermissionEnabled, {
  getSubPermissions,
} from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"
import Switch from "components/Shared/Switch"

const maintenanceModeValues = {
  iOSApp: false,
  androidApp: false,
  website: false,
}

const forceValues = {
  iOSApp: false,
  androidApp: false,
}

const AppSettings = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("App Settings | Sign Burger")

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { loading, isLoading, maintenanceMode, forceAppUpdate } = useSelector(
    state => state.GlobalSettings
  )

  const [formValues, setFormValues] = useState(maintenanceModeValues)
  const [forceAppUpdateValues, setForceAppUpdateValues] = useState(forceValues)

  const [actionType, setActionType] = useState(null)

  const { userRole, accountType } = useSelector(state => state.Login)
  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    edit: null,
    subPermissions: null,
  })

  const [isOnlyCachePermission, setIsOnlyCachePermission] = useState(null)

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      32,
      "app_settings_1",
      accountType
    )

    const isPermissionEdit = isPermissionEnabled(
      userRole,
      32,
      "app_settings_3",
      accountType
    )

    const isSubPermissionEdit = getSubPermissions(
      userRole,
      4,
      "menus_3",
      accountType
    )
    if (
      accountType !== "superadmin" &&
      !isSubPermissionEdit?.clear_menu_cache?.isEnabled &&
      !isPermissionList
    ) {
      navigate("/dashboard")
    } else {
      if (isPermissionList) {
        fetchMaintenanceSettings()
        fetchForceUpdateSettings()
      } else {
        setIsOnlyCachePermission(true)
      }
    }

    setUsersPermissions({
      list: isPermissionList,
      edit: isPermissionEdit,
      subPermissions: isSubPermissionEdit,
    })
  }, [userRole])

  useEffect(() => {
    if (maintenanceMode && maintenanceMode?.maintenanceMode) {
      const { iOSApp, androidApp, website } = maintenanceMode?.maintenanceMode
      setFormValues({
        iOSApp,
        androidApp,
        website,
      })
    }
  }, [maintenanceMode])

  useEffect(() => {
    if (forceAppUpdate && forceAppUpdate?.forceAppUpdate) {
      const { iOSApp, androidApp } = forceAppUpdate?.forceAppUpdate
      setForceAppUpdateValues({
        iOSApp,
        androidApp,
      })
    }
  }, [forceAppUpdate])

  const fetchMaintenanceSettings = () => {
    const payload = {
      type: "maintenanceMode",
    }
    dispatch(getGlobalSettingByType(payload))
  }
  const fetchForceUpdateSettings = () => {
    const payload = {
      type: "forceAppUpdate",
    }
    dispatch(getForceUpdateData(payload))
  }

  const handleMaintenanceModeChange = key => {
    setFormValues(prevValues => ({
      ...prevValues,
      [key]: !prevValues[key],
    }))
  }

  const handleForceUpdateChange = key => {
    setForceAppUpdateValues(prevValues => ({
      ...prevValues,
      [key]: !prevValues[key],
    }))
  }

  const handleMaintenanceMode = () => {
    setActionType("maintenanceMode")
    const { iOSApp, androidApp, website } = formValues
    const payload = {
      id: maintenanceMode._id,
      type: "maintenanceMode",
      maintenanceMode: {
        iOSApp,
        androidApp,
        website,
      },
    }
    dispatch(updateGlobalSettingByType(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
        setActionType(null)
      })
      .catch(error => {})
  }

  const handleForceUpdate = () => {
    setActionType("forceAppUpdate")
    const { iOSApp, androidApp } = forceAppUpdateValues
    const payload = {
      id: forceAppUpdate._id,
      type: "forceAppUpdate",
      forceAppUpdate: {
        iOSApp,
        androidApp,
      },
    }
    dispatch(updateGlobalSettingByType(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
        setActionType(null)
      })
      .catch(error => {})
  }

  const handleClearMenuCache = () => {
    dispatch(clearMenuCache())
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
        setActionType(null)
      })
      .catch(error => {})
  }

  if (loading && !isOnlyCachePermission) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        {usersPermissions.list && (
          <Row>
            <Col lg={6}>
              <Card className="full_height_card">
                <CardBody>
                  <CardTitle>{t("Maintenance Mode")}</CardTitle>
                  <span>
                    {t(
                      "This feature allows you to enable or disable maintenance mode for different platforms, including iOS App, Android App. When enabled, users will be notified that the platform is temporarily unavailable for updates or maintenance"
                    )}
                  </span>
                  <Row>
                    <Col lg={12}>
                      <hr className="mt-3" />
                      <div className="d-flex justify-content-end align-items-center">
                        <Label
                          htmlFor="backgroundEmails"
                          className="col col-form-label"
                        >
                          {t("iOS App")}
                        </Label>
                        <Switch
                          onClickHandler={() =>
                            handleMaintenanceModeChange("iOSApp")
                          }
                          isChecked={formValues.iOSApp}
                          disabled={!usersPermissions.edit}
                        />
                      </div>
                      <hr />
                    </Col>
                    <Col lg={12}>
                      <div className="d-flex justify-content-end align-items-center">
                        <Label
                          htmlFor="androidApp"
                          className="col col-form-label"
                        >
                          {t("Android App")}
                        </Label>
                        <Switch
                          onClickHandler={() =>
                            handleMaintenanceModeChange("androidApp")
                          }
                          isChecked={formValues.androidApp}
                          disabled={!usersPermissions.edit}
                        />
                      </div>
                      <hr />
                    </Col>
                    {/* <Col lg={12}>
                    <div className="d-flex justify-content-end align-items-center">
                      <Label htmlFor="website" className="col col-form-label">
                        {t("Website")}
                      </Label>
                      <Switch
                        onClickHandler={() =>
                          handleMaintenanceModeChange("website")
                        }
                        isChecked={formValues.website}
                        disabled={!usersPermissions.edit}
                      />
                    </div>
                    <hr />
                  </Col> */}
                    {usersPermissions.edit && (
                      <Col lg={12}>
                        <Button
                          color="success"
                          className="d-block ms-auto mt-4"
                          disabled={
                            isLoading && actionType === "maintenanceMode"
                          }
                          onClick={handleMaintenanceMode}
                        >
                          {t("Save")}
                        </Button>
                      </Col>
                    )}
                  </Row>
                </CardBody>
              </Card>
            </Col>
            <Col lg={6}>
              <Card className="full_height_card">
                <CardBody>
                  <CardTitle>{t("Force App Update")}</CardTitle>
                  <span>
                    {t(
                      "This feature ensures that users update their app to the latest version. You can enable or disable forced updates for the iOS and Android platforms, helping maintain compatibility and security."
                    )}
                  </span>
                  <Row>
                    <Col lg={12}>
                      <hr className="mt-3" />
                      <div className="d-flex justify-content-end align-items-center">
                        <Label
                          htmlFor="backgroundEmails"
                          className="col col-form-label"
                        >
                          {t("iOS App")}
                        </Label>
                        <Switch
                          onClickHandler={() =>
                            handleForceUpdateChange("iOSApp")
                          }
                          isChecked={forceAppUpdateValues.iOSApp}
                          disabled={!usersPermissions.edit}
                        />
                      </div>
                      <hr />
                    </Col>
                    <Col lg={12}>
                      <div className="d-flex justify-content-end align-items-center">
                        <Label
                          htmlFor="androidApp"
                          className="col col-form-label"
                        >
                          {t("Android App")}
                        </Label>
                        <Switch
                          onClickHandler={() =>
                            handleForceUpdateChange("androidApp")
                          }
                          isChecked={forceAppUpdateValues.androidApp}
                          disabled={!usersPermissions.edit}
                        />
                      </div>
                      <hr />
                    </Col>
                    {usersPermissions.edit && (
                      <Col lg={12}>
                        <Button
                          color="success"
                          className="d-block ms-auto mt-4"
                          disabled={
                            isLoading && actionType === "forceAppUpdate"
                          }
                          onClick={handleForceUpdate}
                        >
                          {t("Save")}
                        </Button>
                      </Col>
                    )}
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
        <Row>
          {(usersPermissions?.subPermissions?.clear_menu_cache?.isEnabled ||
            accountType === "superadmin") && (
            <Col lg={6}>
              <Card className="full_height_card">
                <CardBody>
                  <CardTitle>{t("Clear Menu Cache")}</CardTitle>
                  <span>
                    {t(
                      "This feature allows you to clear the cached menu data for the application. It ensures that users can view the most recent updates to the menu, such as new items, changes in pricing, or any modifications made by the admin. This can be enabled or disabled for both iOS and Android platforms, providing a seamless and up-to-date experience for users."
                    )}
                  </span>
                  <Col lg={12}>
                    <Button
                      color="success"
                      className="d-block ms-auto mt-4"
                      disabled={isLoading}
                      onClick={handleClearMenuCache}
                    >
                      {t("Clear Cache")}
                    </Button>
                  </Col>
                </CardBody>
              </Card>
            </Col>
          )}
        </Row>
      </div>
    </div>
  )
}

export default AppSettings
